import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../sessionSlice";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";
import thankYouBg from "../../img/background.png";
import thankYouBgMobile from "../../img/mob.png";

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

/** Mirrors the other pages' mobile-portrait breakpoint, used to pick ty_mob.png. */
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id == null || id === "") return "";
    return String(id).trim();
  } catch {
    return "";
  }
}

function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: 0,
      time: "00:00",
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.total_score ?? row.point ?? 0;
  const n = Number(rawPoints);
  const points = Number.isFinite(n) ? n : 0;
  const rawTime = row.time ?? row.display_time ?? "00:00";
  const time = String(rawTime).trim() || "00:00";
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
        ? String(row.userid)
        : "";

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? index),
    rank,
    userId,
    name: String(name),
    points,
    time,
  };
}

const LEADERBOARD_TOP_N = 5;

function buildDisplayRows(rows, currentUserId) {
  if (!rows.length) return [];

  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const top = sorted
    .filter((r) => r.rank >= 1 && r.rank <= LEADERBOARD_TOP_N)
    .slice(0, LEADERBOARD_TOP_N);

  if (!currentUserId) return top;

  const userRow = sorted.find(
    (r) => r.userId && String(r.userId) === String(currentUserId)
  );

  if (!userRow || userRow.rank <= LEADERBOARD_TOP_N) return top;

  return [...top, { ...userRow, isPinnedViewer: true }];
}

function RankDisplay({ rank }) {
  if (rank === 1) {
    return (
      <span className="lb-rank-badge lb-rank-badge--gold" aria-label="Rank 1">
        1
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="lb-rank-badge lb-rank-badge--silver" aria-label="Rank 2">
        2
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="lb-rank-badge lb-rank-badge--bronze" aria-label="Rank 3">
        3
      </span>
    );
  }
  return <span className="lb-rank-badge">{rank}</span>;
}

function Leaderboard() {
  const adminToken = useSelector(selectAdminToken);
  const isMobilePortrait = useMobilePortrait();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };

    body.classList.remove("common-bg");
    body.classList.add("leaderboard-page");
    body.style.backgroundImage = `url("${isMobilePortrait ? thankYouBgMobile : thankYouBg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";

    return () => {
      body.classList.remove("leaderboard-page");
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, [isMobilePortrait]);

  const loadLeaderboard = useCallback(async () => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    setLoading(true);
    setError(null);
    const uid = readSessionUserId();
    setCurrentUserId(uid);
    try {
      const res = await fetch(`${backendBase}/getLeaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid || undefined,
          limit: LEADERBOARD_TOP_N,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setRows(data.data.map(normalizeLeaderboardRow));
      } else {
        setError(data.error || data.message || "Failed to load leaderboard.");
      }
    } catch (e) {
      setError(e.message || "Failed to load leaderboard.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, backendBase]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const displayRows = useMemo(
    () => buildDisplayRows(rows, currentUserId),
    [rows, currentUserId]
  );

  const yourRow = useMemo(() => {
    if (!currentUserId) return null;
    return rows.find((r) => r.userId && String(r.userId) === String(currentUserId));
  }, [rows, currentUserId]);

  const yourRank = yourRow?.rank ?? null;

  return (
    <div className="lb-page">
      <div className="lb-shell">
        <header className="lb-banner-wrap">
          <h1 className="lb-banner">
            <span className="lb-banner__text">LEADERBOARD</span>
          </h1>
          <p className="lb-subtitle">
            See how you stack up against others on this 
            journey.
          </p>
        </header>

        <div className="lb-layout">
          <section className="lb-main">
            <div className="lb-list-card">
              {loading ? (
                <p className="lb-status">Loading scores…</p>
              ) : error ? (
                <p className="lb-status lb-status--error">{error}</p>
              ) : rows.length === 0 ? (
                <p className="lb-status">No scores yet.</p>
              ) : (
                <div className="lb-table">
                  <div className="lb-table__head" aria-hidden>
                    <span className="lb-col lb-col--rank">Rank</span>
                    <span className="lb-col lb-col--name">Name</span>
                    <span className="lb-col lb-col--points">Points</span>
                    <span className="lb-col lb-col--time">Time</span>
                  </div>
                  <ul className="lb-list">
                    {displayRows.map((r) => {
                      const isYou =
                        currentUserId &&
                        r.userId &&
                        String(r.userId) === String(currentUserId);
                      return (
                        <li
                          key={r.isPinnedViewer ? `pinned-${r.key}` : r.key}
                          className={`lb-row${isYou ? " lb-row--you" : ""}${
                            r.isPinnedViewer ? " lb-row--pinned" : ""
                          }`}
                        >
                          <div className="lb-row__rank">
                            <RankDisplay rank={r.rank} />
                          </div>
                          <div className="lb-row__name">{r.name}</div>
                          <span className="lb-row__score">
                            <span className="lb-row__score-pill">
                              {r.points.toLocaleString()}
                            </span>
                          </span>
                          <span className="lb-row__time">
                            <span className="lb-row__time-pill">{r.time}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </section>

          <aside className="lb-aside">
            <div className="lb-rank-card">
              <div className="lb-rank-card__star" aria-hidden>
                <svg viewBox="0 0 48 48" width="40" height="40">
                  <path
                    d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                    fill="#c47810"
                    transform="translate(0 2.5)"
                  />
                  <path
                    d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                    fill="url(#lbStarFill)"
                    stroke="#e8a010"
                    strokeWidth="0.8"
                  />
                  <defs>
                    <linearGradient id="lbStarFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fff3a0" />
                      <stop offset="45%" stopColor="#ffd54a" />
                      <stop offset="100%" stopColor="#f0a020" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <p className="lb-rank-card__label">Your Rank</p>
              <p className="lb-rank-card__value">
                {yourRank != null ? yourRank : "—"}
              </p>
              {yourRow ? (
                <div className="lb-rank-card__stats">
                  <span className="lb-rank-card__stat">
                    {yourRow.points.toLocaleString()} pts
                  </span>
                  <span className="lb-rank-card__stat">{yourRow.time}</span>
                </div>
              ) : (
                <p className="lb-rank-card__hint">Keep playing!</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
